import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ProgressBar, StatusChip, Button } from '@lms/ui';
import { Loader2, Compass, Bookmark, ArrowRight, Star, BookOpen } from 'lucide-react';
import { useEnrollments } from '../hooks/useEnrollments';
import { usePrograms } from '../hooks/usePrograms';
import { useRegistrations } from '../hooks/useRegistrations';
import { useAuthStore } from '../store/auth';
import type { Program, ProgramRegistration, RegistrationStatus } from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { apiBaseUrl } from '../lib/api-client';

export function Programs() {
  const { user } = useAuthStore();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<'MY' | 'EXPLORE'>('MY');

  const { data: enrollments, isLoading: envLoading } = useEnrollments({ userId: user?.id });
  const { data: allPrograms, isLoading: progLoading } = usePrograms({ status: 'PUBLISHED' });
  const { data: registrationsResponse, isLoading: regLoading } = useRegistrations(user?.id);

  // The api-client's `getByUser` returns { items, meta } (matches the
  // `list` endpoint). Unwrap here so the rest of the page can treat
  // the result as a flat array.
  const registrations: ProgramRegistration[] = registrationsResponse?.items ?? [];

  // APPROVED registrations always have a matching Enrollment row created
  // by handleApprovalEffects(). So the registrations we want to surface in
  // the MY tab are exactly the ones without a matching enrollment:
  //   - PENDING     (waiting for admin approval)
  //   - REVISION    (admin asked for changes; user re-submitted -> reset to PENDING,
  //                  but legacy REVISION rows are also possible)
  //   - REJECTED    (user has not re-submitted yet; surfaces a clear CTA)
  //   - CANCELLED   (rare, but worth surfacing for completeness)
  // The status copy reuses the same `copy.registration.success*Desc` text
  // the Registration page shows in its SUCCESS step, so the wording matches
  // what the user just saw after submitting.
  const enrolledProgramIds = new Set(enrollments?.map(e => e.programId) || []);
  const pendingRegistrations = registrations.filter(
    r => r.program && !enrolledProgramIds.has(r.programId)
  );
  const registeredProgramIds = new Set(pendingRegistrations.map(r => r.programId));

  const availablePrograms =
    allPrograms?.items?.filter(
      (p: Program) => !enrolledProgramIds.has(p.id) && !registeredProgramIds.has(p.id)
    ) || [];

  const activeEnrollments = useMemo(
    () => enrollments?.filter(enrollment => enrollment.status === 'ACTIVE') ?? [],
    [enrollments]
  );
  const featuredEnrollment = activeEnrollments[0] ?? enrollments?.[0];

  /**
   * Status-label helper for the StatusChip on each pending-registration
   * card. Reads the bilingual map under `copy.common.registrationStatus`
   * (PENDING / APPROVED / REJECTED / REVISION / CANCELLED) and picks
   * the string for the active language.
   */
  const getRegistrationStatusLabel = (status: RegistrationStatus) =>
    t(copy.common.registrationStatus[status]);

  /**
   * StatusChip colour variant. PENDING / REVISION share the same warning
   * (amber) tone so the user understands "action required" is similar;
   * APPROVED is success (green); REJECTED is error (red); CANCELLED is
   * neutral (grey) since it's a terminal-non-actionable state.
   */
  const getRegistrationStatusVariant = (
    status: RegistrationStatus
  ): 'success' | 'warning' | 'error' | 'neutral' => {
    switch (status) {
      case 'PENDING':
      case 'REVISION':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'CANCELLED':
      default:
        return 'neutral';
    }
  };

  /**
   * Reuse the same long-form copy the SUCCESS step on the Registration
   * page shows, so the MY tab mirrors the language the user just saw.
   * CANCELLED falls back to the REJECTED copy because there is no
   * CANCELLED-specific success card and the user-action is identical
   * (re-submit if the program is still open).
   */
  const getRegistrationStatusDesc = (status: RegistrationStatus) => {
    switch (status) {
      case 'PENDING':
        return t(copy.registration.successPendingDesc);
      case 'APPROVED':
        return t(copy.registration.successApprovedDesc);
      case 'REJECTED':
        return t(copy.registration.successRejectedDesc);
      case 'REVISION':
        return t(copy.registration.successRevisionDesc);
      case 'CANCELLED':
        return t(copy.registration.successRejectedDesc);
      default:
        return '';
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (envLoading || progLoading || regLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.programs.syncingPath} />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      <section className="relative overflow-hidden rounded-[2rem] bg-surface-container-low p-6 sm:p-8">
        <div className="relative z-10 max-w-2xl">
          <h1 className="text-4xl font-headline font-bold text-on-surface mb-2 tracking-tight">
            <Bilingual {...copy.programs.journeyTitle} />
          </h1>
          <p className="text-on-surface-variant max-w-lg leading-relaxed">
            <Bilingual {...copy.programs.journeySubtitle} />
          </p>
        </div>
        <div className="absolute top-0 right-0 p-8 text-primary/10" aria-hidden="true">
          <Star size={120} />
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <div className="rounded-2xl bg-surface-container-lowest p-4 shadow-level1">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant">
            <Bilingual {...copy.programs.activePrograms} />
          </p>
          <p className="mt-1 text-2xl font-headline font-bold text-primary">
            {activeEnrollments.length}
          </p>
        </div>
        <div className="rounded-2xl bg-surface-container-lowest p-4 shadow-level1">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant">
            <Bilingual {...copy.programs.pendingApplications} />
          </p>
          <p className="mt-1 text-2xl font-headline font-bold text-on-surface">
            {pendingRegistrations.length}
          </p>
        </div>
      </div>

      {/* Modern Glass Tabs */}
      <div
        className="flex w-full gap-2 rounded-2xl bg-surface-container-low p-1 sm:w-fit"
        role="tablist"
      >
        <button
          onClick={() => setActiveTab('MY')}
          role="tab"
          aria-selected={activeTab === 'MY'}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-label transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 sm:flex-none ${
            activeTab === 'MY'
              ? 'bg-primary text-on-primary shadow-level1'
              : 'text-on-surface-variant hover:bg-surface-container-high'
          }`}
        >
          <Bookmark size={18} />
          <Bilingual {...copy.programs.myProgramsTab} />
        </button>
        <button
          onClick={() => setActiveTab('EXPLORE')}
          role="tab"
          aria-selected={activeTab === 'EXPLORE'}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-label transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 sm:flex-none ${
            activeTab === 'EXPLORE'
              ? 'bg-primary text-on-primary shadow-level1'
              : 'text-on-surface-variant hover:bg-surface-container-high'
          }`}
        >
          <Compass size={18} />
          <Bilingual {...copy.programs.exploreTab} />
        </button>
      </div>

      {activeTab === 'MY' ? (
        <div className="space-y-6">
          {featuredEnrollment?.program && (
            <Link
              to={`/programs/${featuredEnrollment.program.id}`}
              className="group grid min-w-0 grid-cols-1 gap-5 overflow-hidden rounded-[2rem] bg-primary p-5 text-on-primary no-underline shadow-level2 transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] sm:p-6 lg:grid-cols-[minmax(0,1fr)_18rem]"
            >
              <div className="flex min-w-0 flex-col justify-between gap-6">
                <div>
                  <p className="mb-2 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-secondary">
                    <Bilingual {...copy.programs.ongoing} />
                  </p>
                  <h2 className="m-0 break-words text-2xl font-headline font-bold leading-tight sm:text-3xl">
                    {featuredEnrollment.program.name}
                  </h2>
                  <p className="mt-3 line-clamp-2 max-w-2xl text-sm leading-relaxed text-on-primary/80">
                    {featuredEnrollment.program.description || t(copy.common.noDescription)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-lg bg-on-primary/10 px-4 py-2 text-xs font-bold whitespace-nowrap">
                    <Bilingual {...copy.materials.continueStudy} />
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-secondary">
                    {Math.round(featuredEnrollment.progress)}% <ArrowRight size={14} />
                  </span>
                </div>
              </div>
              <div className="min-h-44 overflow-hidden rounded-2xl bg-on-primary/10">
                {featuredEnrollment.program.coverImage ? (
                  <img
                    src={resolveMediaUrl(featuredEnrollment.program.coverImage, apiBaseUrl)}
                    alt={featuredEnrollment.program.name}
                    className="h-full w-full object-cover opacity-90 transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-secondary">
                    <BookOpen size={42} aria-hidden="true" />
                  </div>
                )}
              </div>
            </Link>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {enrollments?.map(enrollment => {
              const program = enrollment.program;
              if (!program || enrollment.id === featuredEnrollment?.id) return null;

              return (
                <Link
                  key={enrollment.id}
                  to={`/programs/${program.id}`}
                  className="group flex flex-col overflow-hidden rounded-3xl bg-surface-container-lowest p-6 no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                >
                  {/* Program Cover Image / Fallback */}
                  {program.coverImage ? (
                    <div className="relative rounded-2xl overflow-hidden aspect-[16/9] mb-4 bg-surface-container-low">
                      <img
                        src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
                        alt={program.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="relative rounded-2xl overflow-hidden aspect-[16/9] mb-4 bg-gradient-to-br from-primary/15 via-primary/5 to-surface-container-low flex items-center justify-center text-primary/40 group-hover:text-primary/60 transition-colors">
                      <BookOpen size={36} />
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-3 gap-2">
                    <h3 className="font-headline font-bold text-on-surface text-lg group-hover:text-primary transition-colors tracking-tight line-clamp-1">
                      {program.name}
                    </h3>
                    <StatusChip
                      label={
                        enrollment.status === 'ACTIVE'
                          ? t(copy.programs.ongoing)
                          : enrollment.status === 'COMPLETED'
                            ? t(copy.results.completed)
                            : enrollment.status
                      }
                      variant={enrollment.status === 'COMPLETED' ? 'success' : 'warning'}
                    />
                  </div>

                  <div className="flex-1">
                    <p className="text-on-surface-variant text-sm mb-6 line-clamp-2 leading-relaxed opacity-80">
                      {program.description || t(copy.common.noDescription)}
                    </p>
                  </div>

                  <div className="mt-2 space-y-4 rounded-2xl bg-surface-container-low p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-label text-on-surface-variant uppercase tracking-widest font-bold">
                        <Bilingual {...copy.programs.progress} />
                      </span>
                      <span className="text-sm font-semibold text-on-surface">
                        {Math.round(enrollment.progress)}%
                      </span>
                    </div>
                    <ProgressBar value={enrollment.progress} size="sm" />
                  </div>
                </Link>
              );
            })}
            {/*
            Pending / non-approved registration cards. These mirror the
            same status copy the Registration page shows in its SUCCESS
            step so the user sees consistent wording between submitting
            and revisiting the MY tab. APPROVED registrations are hidden
            here because they have a matching Enrollment row above.
          */}
            {pendingRegistrations.map(registration => {
              const program = registration.program;
              if (!program) return null;
              // The card was a single outer Link to the program detail.
              // That made it impossible to add a sibling CTA that deep-links
              // to the Registration form for the same program without
              // nesting anchors (invalid HTML). Restructure the card to a
              // plain <div> with the hover/group styling, an inner <Link>
              // wrapping the title+description (so clicking them still goes
              // to the program detail), and a separate <Link> for the
              // "Lihat Detail Pendaftaran" CTA below the timestamp.
              const isResubmittable =
                registration.status === 'REJECTED' ||
                registration.status === 'REVISION' ||
                registration.status === 'CANCELLED';
              return (
                <div
                  key={registration.id}
                  className="group flex flex-col overflow-hidden rounded-3xl bg-surface-container-lowest p-6 shadow-level1 transition-colors hover:bg-surface-container-high"
                >
                  {/* Program Cover Image / Fallback */}
                  {program.coverImage ? (
                    <div className="relative rounded-2xl overflow-hidden aspect-[16/9] mb-4 bg-surface-container-low">
                      <img
                        src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
                        alt={program.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="relative rounded-2xl overflow-hidden aspect-[16/9] mb-4 bg-gradient-to-br from-primary/15 via-primary/5 to-surface-container-low flex items-center justify-center text-primary/40 group-hover:text-primary/60 transition-colors">
                      <BookOpen size={36} />
                    </div>
                  )}

                  <Link to={`/programs/${program.id}`} className="block no-underline flex-1">
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <h3 className="font-headline font-bold text-on-surface text-lg group-hover:text-primary transition-colors tracking-tight line-clamp-1">
                        {program.name}
                      </h3>
                      <StatusChip
                        label={getRegistrationStatusLabel(registration.status)}
                        variant={getRegistrationStatusVariant(registration.status)}
                      />
                    </div>

                    <p className="text-on-surface-variant text-sm mb-6 leading-relaxed opacity-80 line-clamp-2">
                      {getRegistrationStatusDesc(registration.status)}
                    </p>
                  </Link>

                  <div className="mt-2 space-y-4 rounded-2xl bg-surface-container-low p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-label text-on-surface-variant uppercase tracking-widest font-bold">
                        <Bilingual {...copy.programs.submittedOn} />
                      </span>
                      <span className="text-sm font-semibold text-on-surface">
                        {formatDate(registration.registeredAt)}
                      </span>
                    </div>
                    {registration.lastResubmittedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-label text-on-surface-variant uppercase tracking-widest font-bold">
                          <Bilingual {...copy.programs.resubmittedOn} />
                        </span>
                        <span className="text-sm font-semibold text-on-surface">
                          {formatDate(registration.lastResubmittedAt)}
                        </span>
                      </div>
                    )}
                    {isResubmittable && (
                      <Link
                        to={`/programs/${program.code}/register`}
                        className="flex h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-xs font-label font-bold uppercase tracking-widest text-on-primary no-underline transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                        aria-label={t(copy.programs.viewDetailCta)}
                      >
                        <Bilingual {...copy.programs.viewDetailCta} />
                        <ArrowRight size={16} />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
            {enrollments?.length === 0 && pendingRegistrations.length === 0 && (
              <div className="col-span-full py-20 text-center space-y-4">
                <div className="bg-surface-container rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Bookmark size={32} className="text-on-surface-variant/30" />
                </div>
                <h3 className="text-lg font-headline font-semibold">
                  <Bilingual {...copy.programs.noEnrollmentsTitle} />
                </h3>
                <p className="text-sm text-on-surface-variant max-w-sm mx-auto">
                  <Bilingual {...copy.programs.noEnrollmentsDesc} />
                </p>
                <Button
                  onClick={() => setActiveTab('EXPLORE')}
                  variant="secondary"
                  className="mt-4"
                >
                  <Bilingual {...copy.programs.exploreProgramsBtn} />
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {availablePrograms.map(program => (
            <div
              key={program.id}
              className="group flex flex-col overflow-hidden rounded-3xl bg-surface-container-lowest p-6 shadow-level1 transition-colors hover:bg-surface-container-high"
            >
              {/* Program Cover Image / Fallback */}
              {program.coverImage ? (
                <div className="relative rounded-2xl overflow-hidden aspect-[16/9] mb-4 bg-surface-container-low">
                  <img
                    src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
                    alt={program.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden aspect-[16/9] mb-4 bg-gradient-to-br from-primary/15 via-primary/5 to-surface-container-low flex items-center justify-center text-primary/40 group-hover:text-primary/60 transition-colors">
                  <BookOpen size={36} />
                </div>
              )}

              <div className="flex-1">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-headline font-bold text-on-surface text-lg group-hover:text-primary transition-colors tracking-tight line-clamp-1">
                    {program.name}
                  </h3>
                </div>
                <p className="text-on-surface-variant text-sm mb-6 line-clamp-2 leading-relaxed opacity-80">
                  {program.description || t(copy.common.noDescription)}
                </p>
              </div>

              <div className="mt-2 flex items-center justify-between rounded-2xl bg-surface-container-low p-4">
                <div className="space-y-0.5">
                  <p className="text-[10px] font-label text-on-surface-variant uppercase tracking-tighter">
                    <Bilingual {...copy.common.code} />
                  </p>
                  <p className="text-xs font-mono font-bold">{program.code}</p>
                </div>
                {program.isRegistrationOpen ? (
                  <Link
                    to={`/programs/${program.code}/register`}
                    className="group flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-xs font-label font-bold uppercase tracking-widest text-on-primary no-underline transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                  >
                    <Bilingual {...copy.programs.enrollNow} />
                    <ArrowRight
                      size={16}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </Link>
                ) : (
                  <div className="bg-surface-container-high text-on-surface-variant/50 h-9 px-4 rounded-lg flex items-center gap-2 text-[10px] font-label font-bold uppercase tracking-widest cursor-not-allowed">
                    <Bilingual {...copy.programs.regClosed} />
                  </div>
                )}
              </div>
            </div>
          ))}
          {availablePrograms.length === 0 && (
            <div className="col-span-full py-20 text-center italic text-on-surface-variant">
              <Bilingual {...copy.programs.checkLater} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
